import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AsyncSelect from "react-select/async";
import { getTrans as t } from "../../Localization/translations";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import Helper from "../../Helper";
import CreditMemoService from "../../services/CreditMemo/creditMemoService";
import lineOfBusinessService from "../../services/company/lineofbusinessService";
import subcompanyService from "../../services/company/subcompanyService";
import axios from "axios";

const REASON_OPTIONS = [
    { value: "Billing Error", label: "Billing Error" },
    { value: "Duplicate Invoice", label: "Duplicate Invoice" },
    { value: "Overpayment", label: "Overpayment" },
    { value: "Service Not Rendered", label: "Service Not Rendered" },
    { value: "Discount Applied", label: "Discount Applied" },
    { value: "Other", label: "Other" },
];

export default function AddCreditMemo() {
    const navigate = useNavigate();

    const [creditMemoDate, setCreditMemoDate] = useState(new Date());
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [description, setDescription] = useState("");
    const [reference, setReference] = useState("");
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [companyName, setCompanyName] = useState("");
    const [lobList, setLobList] = useState([]);
    const [lineOfBusiness, setLineOfBusiness] = useState("");
    const [subcompanyList, setSubcompanyList] = useState([]);
    const [subcompany, setSubcompany] = useState("");

    // ── Load company info + LOBs on mount ─────────────────────────
    useEffect(() => {
        // Get company name from user profile
        axios.get(API + "company/companyprofile/", { headers: authHeader() })
            .then((res) => setCompanyName(res.data?.company_name || res.data?.name || ""))
            .catch(() => { });

        // Get LOBs
        lineOfBusinessService.getLineOfBusiness()
            .then((res) => {
                const data = res.data?.data || res.data || [];

                setLobList(data);

                // Set default LOB
                const defaultLob = data.find(
                    (lob) => lob.default === true || lob.is_default === true
                );

                if (defaultLob) {
                    setLineOfBusiness(defaultLob.id);
                }
            })
            .catch(() => { });

        // Get SubCompanies
        subcompanyService.getSubcompany()
            .then((res) => {
                const data = res.data?.data || res.data || [];

                setSubcompanyList(data);

                // Set default SubCompany
                const defaultCompany = data.find(
                    (company) => company.default === true || company.is_default === true
                );

                if (defaultCompany) {
                    setSubcompany(defaultCompany.id);
                }
            })
            .catch(() => { });
    }, []);




    // ── Customer loader ────────────────────────────────────────────
    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await axios.get(
                API + `parties/vendor/?party_type=Customer&&customer_name=${inputValue}`,
                { headers: authHeader() }
            );
            return res.data.data || [];
        } catch (e) {
            return [];
        }
    };

    // ── Submit ─────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCustomer) { toast.error("Please select a customer"); return; }
        if (!amount || parseFloat(amount) <= 0) { toast.error("Amount must be greater than 0"); return; }

        const payload = {
            credit_memo_date: Helper.DatePicker(creditMemoDate),
            customer: selectedCustomer.id,
            amount: parseFloat(amount),
            reason,
            description,
            reference,
            ...(lineOfBusiness ? { line_of_business: lineOfBusiness } : {}),
            ...(subcompany ? { subcompany: subcompany } : {}),
        };

        try {
            const res = await CreditMemoService.create(payload);
            const newId = res.data.id;

            if (attachmentFiles.length > 0 && newId) {
                for (let file of attachmentFiles) {
                    await CreditMemoService.uploadAttachment(newId, file);
                }
            }

            toast.success("Credit Memo created successfully");
            setTimeout(() => navigate("/creditmemo"), 1500);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                JSON.stringify(err?.response?.data) ||
                "Failed to create credit memo"
            );
        }
    };

    return (
        <div className="container-fluid">
            <ToastContainer />

            <div className="header mb-3 pt-2">
                <h5 className="tc">{t("Add Credit Memo")}</h5>
                <h6>
                    <Link to="/creditmemo" style={{ color: "grey" }}>{t("Credit Memos")}</Link>
                    {" "} / <span className="tc">{t("New Credit Memo")}</span>
                </h6>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="row">


                            {/* ── Sub Company ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Sub Company")}</label>
                                    <div className="col-lg-12">
                                        <select
                                            className="form-control"
                                            value={subcompany}
                                            onChange={(e) => setSubcompany(e.target.value)}
                                        >
                                            <option value="">{t("Select Sub Company")}</option>
                                            {subcompanyList.map((sc) => (
                                                <option key={sc.id} value={sc.id}>
                                                    {sc.sub_company_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            {/* ── Line of Business ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Line of Business")}</label>
                                    <div className="col-lg-12">
                                        <select
                                            className="form-control"
                                            value={lineOfBusiness}
                                            onChange={(e) => setLineOfBusiness(e.target.value)}
                                        >
                                            <option value="">{t("Select Line of Business")}</option>
                                            {lobList.map((lob) => (
                                                <option key={lob.id} value={lob.id}>
                                                    {lob.lob_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>


                            {/* ── Credit Memo Date ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label required">{t("Credit Memo Date")}</label>
                                    <div className="col-lg-12">
                                        <DatePicker
                                            className="form-control datepick"
                                            selected={creditMemoDate}
                                            onChange={(d) => setCreditMemoDate(d)}
                                            dateFormat="MM/dd/yyyy"
                                            showYearDropdown scrollableYearDropdown showMonthDropdown dropdownMode="select"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Customer ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label required">{t("Customer Name")}</label>
                                    <div className="col-lg-12">
                                        <AsyncSelect
                                            cacheOptions
                                            defaultOptions
                                            isClearable
                                            placeholder={t("Search Customer")}
                                            value={selectedCustomer}
                                            loadOptions={loadCustomerOptions}
                                            onChange={setSelectedCustomer}
                                            getOptionLabel={(e) => e.customer_name}
                                            getOptionValue={(e) => e.id}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Amount ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label required">{t("Amount")}</label>
                                    <div className="col-lg-12">
                                        <input
                                            className="form-control"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Reason ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Reason")}</label>
                                    <div className="col-lg-12">
                                        <select
                                            className="form-control"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        >
                                            <option value="">{t("Select Reason")}</option>
                                            {REASON_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* ── Reference ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Reference")}</label>
                                    <div className="col-lg-12">
                                        <input
                                            className="form-control"
                                            value={reference}
                                            onChange={(e) => setReference(e.target.value)}
                                            placeholder={t("Reference")}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Attachments ── */}
                            <div className="col-xl-6">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Attachments")}</label>
                                    <div className="col-lg-12">
                                        <input
                                            type="file"
                                            className="form-control"
                                            multiple
                                            onChange={(e) => setAttachmentFiles(Array.from(e.target.files))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Description ── */}
                            <div className="col-xl-12">
                                <div className="form-group row">
                                    <label className="col-lg-12 col-form-label">{t("Description")}</label>
                                    <div className="col-lg-12">
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder={t("Description")}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* ── Buttons ── */}
                        <div className="text-left py-3">
                            <button
                                type="button"
                                className="btn-cancel-contract border-0 btn btn-secondary btn-md"
                                onClick={() => navigate("/creditmemo")}
                            >
                                {t("Cancel")}
                            </button>
                            &nbsp;&nbsp;
                            <button
                                type="submit"
                                className="btn-continue border-0 btn btn-primary btn-md"
                            >
                                {t("Save")}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
